import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';
import { Loader, Text, Flex, Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { GET_SURVEY } from '../../../apollo/queries/survey';
import {
    SurveyDetailsHeader,
    SurveyItemsTable,
    SurveyCompetitorDrawer,
    type CompetitorData,
    type OpenProofViewerPayload,
} from '../../../components/survey';
import ProofOfPricingModal from '../../../components/requests/components/ProofOfPricingModal';
import {
    When,
    ConfirmModal,
    UnsavedChangesWarningModal,
} from '../../../components/common';
import { useAuth } from '../../../context/AuthProvider';
import { ReassignSurveyModal } from '../../../components/modals/ReassignSurveyModal';
import { CancelSurveyConfirmModal } from '../../../components/modals/CancelSurveyConfirmModal';
import { AddCompetitorDrawer } from '../../../components/survey/AddCompetitorDrawer';
import { SurveyActivityDrawer } from '../../../components/survey/SurveyActivityDrawer';
import { useSurveyStore } from '../../../stores/SurveyStore';
import {
    useSubmitSurveyToDivisionalManager,
    usePrepopulateSurveyCompetitorPrices,
    usePrepopulateSurveyDmReview,
} from '../../../services/survey/survey.mutation';
import {
    showSuccessNotification,
    showErrorNotification,
} from '../../../notifications';
import {
    getSurveyScopeLabel,
    isSurveyInDivisionalManagerReview,
    isSurveyStoreManagerEditingLocked,
    isSurveyCancellable,
} from '../helpers';
import { CompetitorDrawerData } from '../columns';
import { useExportSurveyItems } from '../../../hooks/useExportSurveyItems';
import { useSurveyPrices } from '../../../hooks/useSurveyPrices';
import type { Competitor } from '../../../codegen/graphql';
import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';
import { SurveyPricesProvider } from '../../../context/SurveyPricesContext';
import { prefetchSurveyProductSales } from '../../../helpers/prefetchSurveyProductSales';
import { useUser } from '../../../hooks/useUser';

const SurveyItemsTableMemo = React.memo(SurveyItemsTable);

export default function SurveyDetailsPage() {
    const { surveyId } = useParams();
    const navigate = useNavigate();
    const { authUser } = useAuth();
    const { isDivisionManager, isAdmin, isStoreManager, isSuperAdmin } =
        useUser();
    const [reassignModalOpened, { open: openReassign, close: closeReassign }] =
        useDisclosure(false);
    const [
        addCompetitorDrawerOpened,
        { open: openAddCompetitor, close: closeAddCompetitor },
    ] = useDisclosure(false);
    const [
        competitorDrawerOpened,
        { open: openCompetitorDrawer, close: closeCompetitorDrawer },
    ] = useDisclosure(false);
    const [
        activityDrawerOpened,
        { open: openActivityDrawer, close: closeActivityDrawer },
    ] = useDisclosure(false);
    const [
        submitConfirmOpened,
        { open: openSubmitConfirm, close: closeSubmitConfirm },
    ] = useDisclosure(false);
    const [
        dmSubmitConfirmOpened,
        { open: openDmSubmitConfirm, close: closeDmSubmitConfirm },
    ] = useDisclosure(false);
    const [
        prepopulateConfirmOpened,
        { open: openPrepopulateConfirm, close: closePrepopulateConfirm },
    ] = useDisclosure(false);
    const [
        removeCompetitorConfirmOpened,
        {
            open: openRemoveCompetitorConfirm,
            close: closeRemoveCompetitorConfirm,
        },
    ] = useDisclosure(false);
    const [
        cancelSurveyConfirmOpened,
        { open: openCancelSurveyConfirm, close: closeCancelSurveyConfirm },
    ] = useDisclosure(false);
    const [competitorToRemove, setCompetitorToRemove] = useState<{
        priceSurveyCompetitorId: string;
        competitorId: string;
        competitorName: string;
    } | null>(null);
    const [selectedCompetitor, setSelectedCompetitor] =
        useState<CompetitorData | null>(null);
    const [proofViewer, setProofViewer] =
        useState<OpenProofViewerPayload | null>(null);
    const [dmProgressResetKey, setDmProgressResetKey] = useState(0);
    const [isPrepopulateRefreshing, setIsPrepopulateRefreshing] =
        useState(false);
    const [divisionalManagerActions, setDivisionalManagerActions] = useState<{
        submitSurvey: () => void;
        canSubmit: boolean;
        isSubmitting: boolean;
        hasChanges: boolean;
    } | null>(null);
    const divisionalManagerActionsRef = useRef(divisionalManagerActions);
    divisionalManagerActionsRef.current = divisionalManagerActions;
    const shouldPrefetchSales = isDivisionManager || isAdmin;
    const apolloClient = useApolloClient();
    const { exporting, exportSurveyItems } = useExportSurveyItems();

    const { survey, setSurvey, setIsLoading } = useSurveyStore();

    useEffect(() => {
        setSurvey(null);
    }, [surveyId, setSurvey]);

    const { data, loading, error, refetch, networkStatus } = useQuery(
        GET_SURVEY,
        {
            variables: { id: surveyId },
            skip: !surveyId,
            notifyOnNetworkStatusChange: true,
        },
    );

    useEffect(() => {
        if (data?.getSurvey) {
            setSurvey(data.getSurvey);
        }
    }, [data?.getSurvey, setSurvey]);

    useEffect(() => {
        setIsLoading(loading);
    }, [loading, setIsLoading]);

    const isRefetching = networkStatus === 4;
    const isTableLoading = loading || isRefetching;

    const handleSaveComplete = useCallback(async () => {
        try {
            const result = await refetch();
            const refreshed = result.data?.getSurvey;
            if (refreshed) {
                setSurvey(refreshed);
            }
        } catch (refreshError) {
            console.error('Survey refresh after save failed', refreshError);
        }
    }, [refetch, setSurvey]);

    const surveyPrices = useSurveyPrices(survey, handleSaveComplete);

    const surveyPricesContextValue = useMemo(
        () => ({
            isSaving: surveyPrices.isSaving,
            changedCount: surveyPrices.changedCount,
            hasAnyChanges: surveyPrices.hasAnyChanges,
            saveChanges: surveyPrices.saveChanges,
        }),
        [
            surveyPrices.isSaving,
            surveyPrices.changedCount,
            surveyPrices.hasAnyChanges,
            surveyPrices.saveChanges,
        ],
    );

    const isDirty =
        surveyPrices.hasAnyChanges ||
        (divisionalManagerActions?.hasChanges ?? false);
    const blocker = useUnsavedChangesPrompt(isDirty);

    const handleRefresh = useCallback(async () => {
        surveyPrices.requestReinit();
        const result = await refetch();
        const refreshed = result.data?.getSurvey;
        if (shouldPrefetchSales && refreshed) {
            try {
                await prefetchSurveyProductSales(apolloClient, refreshed);
            } catch (err) {
                console.error(
                    'Prefetch survey product sales failed (refresh still succeeded)',
                    err,
                );
            }
        }
    }, [
        apolloClient,
        refetch,
        shouldPrefetchSales,
        surveyPrices.requestReinit,
    ]);

    const [submitSurveyToDivisionalManager, { loading: isSubmitting }] =
        useSubmitSurveyToDivisionalManager();
    const [
        prepopulateSurveyCompetitorPrices,
        { loading: isPrepopulatingPrices },
    ] = usePrepopulateSurveyCompetitorPrices();
    const [prepopulateSurveyDmReview, { loading: isPrepopulatingDm }] =
        usePrepopulateSurveyDmReview();
    const isPrepopulating =
        isPrepopulatingPrices || isPrepopulatingDm || isPrepopulateRefreshing;

    const handleExport = useCallback(
        (includeCostPricesAndMargins: boolean) => {
            if (surveyId) {
                exportSurveyItems(surveyId, includeCostPricesAndMargins);
            }
        },
        [surveyId, exportSurveyItems],
    );

    const handleSubmitClick = useCallback(() => {
        const { completionPercent } = useSurveyStore.getState();

        if (completionPercent < 100) {
            return;
        }

        if (!surveyId) {
            showErrorNotification('Survey ID is missing');
            return;
        }

        openSubmitConfirm();
    }, [surveyId, openSubmitConfirm]);

    const handleSubmitConfirm = useCallback(async () => {
        if (!surveyId) return;

        await submitSurveyToDivisionalManager({
            variables: { surveyId },
        });
        surveyPrices.requestReinit();
        await refetch();
        showSuccessNotification(
            'Survey successfully submitted to Divisional Manager',
        );
    }, [
        surveyId,
        submitSurveyToDivisionalManager,
        refetch,
        surveyPrices.requestReinit,
    ]);

    const handleDmSubmitSurveyClick = useCallback(() => {
        openDmSubmitConfirm();
    }, [openDmSubmitConfirm]);

    const handleDmSubmitConfirm = useCallback(async () => {
        await divisionalManagerActionsRef.current?.submitSurvey();
    }, []);

    const canPrepopulateCompetitorPrices =
        isSuperAdmin &&
        (survey?.status === 'NotStarted' || survey?.status === 'InProgress');
    const canPrepopulateDmReview =
        isSuperAdmin && isSurveyInDivisionalManagerReview(survey);
    const canPrepopulate =
        canPrepopulateCompetitorPrices || canPrepopulateDmReview;
    const prepopulateLabel = canPrepopulateDmReview
        ? 'Prepop DM Review'
        : 'Prepop Prices';

    const handlePrepopulateClick = useCallback(() => {
        openPrepopulateConfirm();
    }, [openPrepopulateConfirm]);

    const handlePrepopulateConfirm = useCallback(async () => {
        if (!surveyId) {
            throw new Error('Survey ID is missing');
        }

        setIsPrepopulateRefreshing(true);
        try {
            if (canPrepopulateDmReview) {
                const result = await prepopulateSurveyDmReview({
                    variables: { surveyId },
                });
                const payload = result.data?.prepopulateSurveyDmReview;
                if (!payload?.success) {
                    throw new Error('Failed to prepopulate DM review');
                }
                showSuccessNotification(
                    `Prepopulated DM review for ${payload.updatedCount} items`,
                );
            } else {
                const result = await prepopulateSurveyCompetitorPrices({
                    variables: { surveyId },
                });
                const payload = result.data?.prepopulateSurveyCompetitorPrices;
                if (!payload?.success) {
                    throw new Error('Failed to prepopulate competitor prices');
                }
                showSuccessNotification(
                    `Prepopulated ${payload.updatedCount} competitor prices`,
                );
            }

            surveyPrices.requestReinit();
            const refetchResult = await refetch();
            const refreshedSurvey = refetchResult.data?.getSurvey;
            if (refreshedSurvey) {
                setSurvey(refreshedSurvey);
            }
            if (canPrepopulateDmReview) {
                setDmProgressResetKey(prev => prev + 1);
            }
        } finally {
            setIsPrepopulateRefreshing(false);
        }
    }, [
        surveyId,
        canPrepopulateDmReview,
        prepopulateSurveyDmReview,
        prepopulateSurveyCompetitorPrices,
        surveyPrices.requestReinit,
        refetch,
        setSurvey,
    ]);

    const handleReassign = useCallback(() => {
        openReassign();
    }, [openReassign]);

    const handleReassignSuccess = useCallback(() => {
        surveyPrices.requestReinit();
        refetch();
    }, [refetch, surveyPrices.requestReinit]);

    const handleCancelSurveyClick = useCallback(() => {
        openCancelSurveyConfirm();
    }, [openCancelSurveyConfirm]);

    const handleCancelSurveySuccess = useCallback(() => {
        surveyPrices.requestReinit();
    }, [surveyPrices.requestReinit]);

    const handleAddCompetitor = useCallback(() => {
        openAddCompetitor();
    }, [openAddCompetitor]);

    const handleCompetitorAdd = useCallback(
        (competitor: Competitor) => {
            surveyPrices.addCompetitorLocally(competitor);
        },
        [surveyPrices.addCompetitorLocally],
    );

    const existingCompetitorIds = useMemo(() => {
        return new Set(
            surveyPrices.activeCompetitors?.map(sc => sc.competitorId) || [],
        );
    }, [surveyPrices.activeCompetitors]);

    const handleCompetitorClick = useCallback(
        (competitorDrawerData: CompetitorDrawerData) => {
            setSelectedCompetitor(competitorDrawerData);
            openCompetitorDrawer();
        },
        [openCompetitorDrawer],
    );

    const handleRemoveCompetitorClick = useCallback(
        (priceSurveyCompetitorId: string) => {
            const competitor = surveyPrices.activeCompetitors?.find(
                sc => sc.id === priceSurveyCompetitorId,
            );
            if (!competitor?.competitorId || !competitor.competitor?.name) {
                showErrorNotification('Could not find the selected competitor');
                return;
            }
            const competitorName = competitor.competitor.name;
            setCompetitorToRemove({
                priceSurveyCompetitorId,
                competitorId: competitor.competitorId,
                competitorName,
            });
            openRemoveCompetitorConfirm();
        },
        [surveyPrices.activeCompetitors, openRemoveCompetitorConfirm],
    );

    const handleRemoveCompetitorConfirm = useCallback(() => {
        if (!competitorToRemove) return;
        surveyPrices.removeCompetitorLocally(competitorToRemove.competitorId);
        setCompetitorToRemove(null);
        closeRemoveCompetitorConfirm();
    }, [
        competitorToRemove,
        surveyPrices.removeCompetitorLocally,
        closeRemoveCompetitorConfirm,
    ]);

    const handleRemoveCompetitorConfirmClose = useCallback(() => {
        closeRemoveCompetitorConfirm();
        setCompetitorToRemove(null);
    }, [closeRemoveCompetitorConfirm]);

    const handleCompetitorDrawerClose = useCallback(() => {
        closeCompetitorDrawer();
        setSelectedCompetitor(null);
    }, [closeCompetitorDrawer]);

    const ensurePricesSavedForProofUpload = useCallback(
        async (
            competitorId: string,
            currentPriceSurveyCompetitorId: string,
        ): Promise<string | null> => {
            if (!surveyPrices.hasAnyChanges) {
                return currentPriceSurveyCompetitorId;
            }

            const saved = await surveyPrices.saveChanges();
            if (!saved) {
                return null;
            }

            const refreshedSurvey = useSurveyStore.getState().survey;
            const match = refreshedSurvey?.surveyCompetitors?.find(
                sc => sc.competitorId === competitorId,
            );
            const resolvedPriceSurveyCompetitorId =
                match?.id ?? currentPriceSurveyCompetitorId;

            setSelectedCompetitor(prev => {
                if (
                    !prev ||
                    prev.priceSurveyCompetitorId ===
                        resolvedPriceSurveyCompetitorId
                ) {
                    return prev;
                }
                return {
                    ...prev,
                    priceSurveyCompetitorId: resolvedPriceSurveyCompetitorId,
                };
            });

            return resolvedPriceSurveyCompetitorId;
        },
        [surveyPrices.hasAnyChanges, surveyPrices.saveChanges],
    );

    const handleProofsUpdated = useCallback(() => {
        surveyPrices.requestReinit();
        refetch();
    }, [refetch, surveyPrices.requestReinit]);

    const handleOpenProofViewer = useCallback(
        (payload: OpenProofViewerPayload) => {
            setProofViewer(payload);
        },
        [],
    );

    const handleCloseProofViewer = useCallback(() => {
        setProofViewer(null);
    }, []);

    if (loading && !data) {
        return (
            <Flex h="100%" align="center" justify="center">
                <Loader size="lg" />
            </Flex>
        );
    }

    if (error || !survey) {
        return (
            <Box p="xl">
                <Text c="red">
                    Error loading survey: {error?.message || 'Survey not found'}
                </Text>
            </Box>
        );
    }

    const showReassignModal = reassignModalOpened && !!survey;

    const proofViewerModal =
        proofViewer !== null ? (
            <ProofOfPricingModal
                open
                layout="floating"
                documents={proofViewer.documents}
                initialIndex={proofViewer.initialIndex}
                onClose={handleCloseProofViewer}
            />
        ) : null;

    const isChampion =
        !!authUser?.id &&
        !!survey.championUserId &&
        authUser.id === survey.championUserId;
    const canSubmitToDivisionalManager =
        !isDivisionManager &&
        isChampion &&
        survey.status !== 'Completed' &&
        survey.status !== 'Cancelled';
    const inDivisionalManagerReview = isSurveyInDivisionalManagerReview(survey);
    const showDivisionalManagerSubmitSurvey =
        (isDivisionManager || isAdmin) && inDivisionalManagerReview;
    const canCancelSurvey =
        (isDivisionManager || isAdmin) && isSurveyCancellable(survey);
    const drawerTitle = getSurveyScopeLabel(survey);

    return (
        <Box className="h-full w-full flex flex-col">
            <SurveyDetailsHeader
                survey={survey}
                onBack={() => navigate('/price-surveys')}
                onExport={handleExport}
                hideCostAndMargins={isStoreManager}
                onViewHistory={openActivityDrawer}
                onSubmit={
                    canSubmitToDivisionalManager ? handleSubmitClick : undefined
                }
                onSubmitSurvey={
                    showDivisionalManagerSubmitSurvey &&
                    divisionalManagerActions
                        ? handleDmSubmitSurveyClick
                        : undefined
                }
                canSubmitSurvey={divisionalManagerActions?.canSubmit ?? false}
                isSubmittingSurvey={
                    divisionalManagerActions?.isSubmitting ?? false
                }
                onReassign={isDivisionManager ? handleReassign : undefined}
                onPrepopulate={
                    canPrepopulate ? handlePrepopulateClick : undefined
                }
                canPrepopulate={canPrepopulate}
                isPrepopulating={isPrepopulating}
                prepopulateLabel={prepopulateLabel}
                onCancelSurvey={
                    canCancelSurvey ? handleCancelSurveyClick : undefined
                }
                exporting={exporting}
                isDivisionalManagerReviewing={showDivisionalManagerSubmitSurvey}
            />

            <Box className="flex-1 px-8 py-6 overflow-hidden flex flex-col">
                <SurveyPricesProvider value={surveyPricesContextValue}>
                    <SurveyItemsTableMemo
                        survey={survey}
                        authUser={authUser}
                        isLoading={isTableLoading}
                        onRefresh={handleRefresh}
                        onAddCompetitor={handleAddCompetitor}
                        onCompetitorClick={handleCompetitorClick}
                        onRemoveCompetitor={handleRemoveCompetitorClick}
                        onDivisionalManagerActionsReady={
                            setDivisionalManagerActions
                        }
                        pricesMap={surveyPrices.pricesMap}
                        activeCompetitors={surveyPrices.activeCompetitors}
                        updateCell={surveyPrices.updateCell}
                        dmProgressResetKey={dmProgressResetKey}
                    />
                </SurveyPricesProvider>
            </Box>

            <When condition={showReassignModal}>
                <ReassignSurveyModal
                    survey={survey}
                    opened={reassignModalOpened}
                    onClose={closeReassign}
                    onSuccess={handleReassignSuccess}
                />
            </When>
            <AddCompetitorDrawer
                opened={addCompetitorDrawerOpened}
                onClose={closeAddCompetitor}
                survey={survey}
                existingCompetitorIds={existingCompetitorIds}
                onCompetitorAdd={handleCompetitorAdd}
            />

            <SurveyActivityDrawer
                surveyId={surveyId!}
                opened={activityDrawerOpened}
                onClose={closeActivityDrawer}
                zoneName={drawerTitle}
            />

            <SurveyCompetitorDrawer
                opened={competitorDrawerOpened}
                onClose={handleCompetitorDrawerClose}
                competitorData={selectedCompetitor}
                onProofsUpdated={handleProofsUpdated}
                onOpenProofViewer={handleOpenProofViewer}
                ensurePricesSavedForProofUpload={
                    ensurePricesSavedForProofUpload
                }
                isSavingPrices={surveyPrices.isSaving}
                readOnly={
                    isDivisionManager ||
                    isSurveyStoreManagerEditingLocked(survey)
                }
            />

            <When condition={proofViewer !== null}>{proofViewerModal}</When>

            <ConfirmModal
                opened={submitConfirmOpened}
                onClose={closeSubmitConfirm}
                title="Submit Survey to Divisional Manager?"
                confirmLabel="Submit"
                cancelLabel="Cancel"
                onConfirm={handleSubmitConfirm}
                loading={isSubmitting}
            >
                <Text size="sm" c="dark.7">
                    Are you sure you want to submit this survey to the
                    Divisional Manager? Once submitted, you will not be able to
                    make further changes.
                </Text>
            </ConfirmModal>

            <ConfirmModal
                opened={dmSubmitConfirmOpened}
                onClose={closeDmSubmitConfirm}
                title="Submit survey and generate PCRs?"
                confirmLabel="Submit"
                cancelLabel="Cancel"
                onConfirm={handleDmSubmitConfirm}
                loading={divisionalManagerActions?.isSubmitting ?? false}
            >
                <Text size="sm" c="dark.7">
                    This will create price change requests for items you
                    approved. You will not be able to make further changes. Are
                    you sure you want to continue?
                </Text>
            </ConfirmModal>

            <ConfirmModal
                opened={prepopulateConfirmOpened}
                onClose={closePrepopulateConfirm}
                title={
                    canPrepopulateDmReview
                        ? 'Prepopulate DM review?'
                        : 'Prepopulate competitor prices?'
                }
                confirmLabel="Prepop"
                cancelLabel="Cancel"
                onConfirm={handlePrepopulateConfirm}
                loading={isPrepopulating}
            >
                <Text size="sm" c="dark.7">
                    {canPrepopulateDmReview
                        ? 'This will fill proposed prices using suggested values (~90% approve) and random decline reasons (~10% decline). The divisional manager can still review and submit.'
                        : 'This will fill all competitor price cells with random values. The champion can still review and submit the survey.'}
                </Text>
            </ConfirmModal>

            <ConfirmModal
                opened={removeCompetitorConfirmOpened}
                onClose={handleRemoveCompetitorConfirmClose}
                title="Remove competitor from survey?"
                confirmLabel="Remove"
                cancelLabel="Cancel"
                onConfirm={handleRemoveCompetitorConfirm}
                confirmColor="red"
            >
                <Text size="sm" c="dark.7">
                    Are you sure you want to remove{' '}
                    <Text component="span" fw={700} inherit>
                        {competitorToRemove?.competitorName ??
                            'this competitor'}
                    </Text>{' '}
                    from the survey? This will remove all price entries and
                    proofs for this competitor.
                </Text>
            </ConfirmModal>

            <CancelSurveyConfirmModal
                opened={cancelSurveyConfirmOpened}
                onClose={closeCancelSurveyConfirm}
                surveyId={surveyId ?? null}
                onSuccess={handleCancelSurveySuccess}
            />

            <UnsavedChangesWarningModal
                opened={blocker.state === 'blocked'}
                onStay={() => blocker.reset?.()}
                onLeave={() => blocker.proceed?.()}
            />
        </Box>
    );
}
